import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Users, Calendar, TrendingUp, Award, ArrowRight, Check, Mail, Rss, MapPin, Clock, Share2, Briefcase, Building2, Sparkles, Home, BedDouble, Newspaper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';
import { ShareButtons } from '@/components/ShareButtons';
import { subscribeNewsletter, getExternalFeed, getFileUrl } from '@/lib/api';
import api from '@/lib/api';

const HERO_BG = "https://customer-assets.emergentagent.com/job_ae021ef0-7027-4ae5-b97f-cd7f1827f457/artifacts/z1lv2e2v_P2341000.JPG";
const NETWORKING_IMG = "https://customer-assets.emergentagent.com/job_e6bd1d26-d4b0-49be-b226-7ca558f827f9/artifacts/ww0srqfb_ChatGPT%20Image%20May%2028%2C%202026%2C%2009_28_52%20AM.png";
const PROPERTY_FALLBACK = "https://customer-assets.emergentagent.com/job_e6bd1d26-d4b0-49be-b226-7ca558f827f9/artifacts/9bhssqo2_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2005_04_33%20PM.png";

// Lightweight count-up animation for stat chips
const CountUp = ({ end = 0, duration = 1400, suffix = '' }) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(end * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);
  return <>{value}{suffix}</>;
};

const LandingPage = () => {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [partners, setPartners] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const jobsRes = await api.get('/jobs').catch(() => ({ data: [] }));
      setJobs(jobsRes.data || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
    }

    // News — local business news FIRST, then paisley.org.uk to top up
    try {
      const [localRes, extRes] = await Promise.all([
        api.get('/news').catch(() => ({ data: [] })),
        fetch('https://paisley.org.uk/api/posts', { cache: 'no-store' })
          .then(r => r.ok ? r.json() : [])
          .catch(() => []),
      ]);
      const local = (localRes.data || [])
        .filter(p => p.is_published !== false)
        .map(p => ({
          id: p.id, title: p.title, slug: p.slug,
          excerpt: p.excerpt || p.meta_description || '',
          featured_image: p.cover_image,
          category: p.tags?.[0] || 'business',
          created_at: p.created_at,
          origin: 'local',
        }));
      const external = (Array.isArray(extRes) ? extRes : [])
        .filter(p => p.published !== false)
        .filter(p => {
          const c = (p.category || '').toLowerCase();
          return c !== 'announcements' && c !== 'events';
        })
        .map(p => ({ ...p, origin: 'paisley.org.uk' }));
      // Local first, external fills the rest — capped at 6 for homepage strip
      setNews([...local, ...external].slice(0, 6));
    } catch {
      setNews([]);
    }

    // Events — live from paisley.org.uk /api/events/feed
    try {
      const res = await fetch('https://paisley.org.uk/api/events/feed?limit=8', { cache: 'no-store' });
      if (!res.ok) throw new Error('events failed');
      const data = await res.json();
      setEvents(Array.isArray(data) ? data.slice(0, 6) : []);
    } catch {
      setEvents([]);
    }

    // Advertisers strip — full advertiser list mirrored from paisley.org.uk
    try {
      const res = await fetch(
        'https://paisley.org.uk/api/advertisers/feed',
        { cache: 'no-store' },
      );
      if (!res.ok) throw new Error('advertisers feed failed');
      const data = await res.json();
      const ads = Array.isArray(data?.ads) ? data.ads : (Array.isArray(data) ? data : []);
      setPartners(ads);
    } catch {
      setPartners([]);
    }

    // Properties to Let — live mirror for the homepage preview strip
    try {
      const res = await fetch(
        'https://paisley.org.uk/api/properties/feed?limit=6',
        { cache: 'no-store' },
      );
      if (!res.ok) throw new Error('properties feed failed');
      const data = await res.json();
      setProperties(Array.isArray(data) ? data.slice(0, 4) : []);
    } catch {
      setProperties([]);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { value: '50+', label: 'Active Sponsors', icon: Users },
    { value: '100+', label: 'Events Yearly', icon: Calendar },
    { value: '25K+', label: 'Monthly Visitors', icon: TrendingUp },
    { value: '95%', label: 'Satisfaction Rate', icon: Award },
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <SEOPage.Home />
      <Navbar transparent />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="animate-fade-in-up">
            <Badge className="mb-6 bg-sky-500/20 text-sky-400 border-sky-500/30 hover:bg-sky-500/30">
              Paisley's Premier Business Platform
            </Badge>
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 animate-fade-in-up animation-delay-100 tracking-tight leading-none">
            The Heart of<br />
            <span className="text-sky-400">Paisley's Business Community</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-200 leading-relaxed">
            Local news, jobs, business directory and events — everything Paisley's
            ambitious businesses need to grow across Renfrewshire and beyond.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-300">
            <Link to="/sponsors" data-testid="hero-cta-primary">
              <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white shadow-lg shadow-sky-500/30 px-8 py-6 text-lg">
                Promote Your Latest Event
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/sponsors" data-testid="hero-cta-secondary">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                View Packages
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-900" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.label}
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-sky-500/20 rounded-xl mb-4">
                  <stat.icon className="h-7 w-7 text-sky-400" />
                </div>
                <div className="font-accent text-4xl font-semibold text-white tracking-wide">{stat.value}</div>
                <div className="text-slate-400 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jobs Section — striking, dynamic, above the fold-2 */}
      {/* Jobs — CORE product, prime hero placement */}
      {jobs.length > 0 && (
        <section
          className="relative py-24 lg:py-28 overflow-hidden bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900"
          data-testid="jobs-section"
        >
          {/* Ambient glows + subtle grid overlay for premium feel */}
          <div className="absolute -top-40 -right-24 h-[500px] w-[500px] rounded-full bg-sky-500/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-40 -left-24 h-[500px] w-[500px] rounded-full bg-indigo-500/25 blur-3xl pointer-events-none" />
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Hero heading + live stats banner */}
            <div className="text-center mb-12">
              <Badge className="mb-5 bg-emerald-500/15 text-emerald-300 border-emerald-400/30 backdrop-blur px-4 py-1.5">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                Live · Updated daily
              </Badge>
              <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-none">
                Find your next job in <span className="bg-gradient-to-r from-sky-400 via-cyan-300 to-indigo-400 bg-clip-text text-transparent">Paisley</span>
              </h2>
              <p className="mt-6 text-slate-300 text-lg max-w-2xl mx-auto">
                Real vacancies from Renfrewshire Council, NHS, local employers and trusted partners — hand-picked and refreshed every morning.
              </p>

              {/* Live stats banner */}
              <div className="mt-10 flex flex-wrap items-center justify-center gap-8 sm:gap-14">
                <div className="text-center" data-testid="stat-live-jobs">
                  <div className="font-heading text-4xl sm:text-5xl font-bold text-white">
                    <CountUp end={jobs.length} />
                  </div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Live jobs</div>
                </div>
                <div className="hidden sm:block h-12 w-px bg-white/10" />
                <div className="text-center">
                  <div className="font-heading text-4xl sm:text-5xl font-bold text-white">
                    <CountUp end={new Set(jobs.map(j => j.employer).filter(Boolean)).size} />
                  </div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Employers hiring</div>
                </div>
                <div className="hidden sm:block h-12 w-px bg-white/10" />
                <div className="text-center">
                  <div className="font-heading text-4xl sm:text-5xl font-bold text-white">
                    <CountUp end={jobs.filter(j => {
                      if (!j.created_at) return false;
                      const d = new Date(j.created_at);
                      const week = Date.now() - 7 * 864e5;
                      return d.getTime() > week;
                    }).length} />
                  </div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Added this week</div>
                </div>
              </div>

              {/* Category quick-filter chips */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                {['Full-time', 'Part-time', 'Council', 'NHS', 'Apprenticeship', 'Retail', 'Hospitality'].map((tag) => (
                  <Link
                    key={tag}
                    to={`/jobs?q=${encodeURIComponent(tag)}`}
                    className="px-4 py-1.5 rounded-full text-xs font-medium bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 hover:border-sky-400/40 transition-all"
                    data-testid={`jobs-quick-filter-${tag.toLowerCase()}`}
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>

            {/* Jobs grid — 1 large hero + 4 companions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 mb-10">
              {jobs.slice(0, 5).map((job, i) => {
                const isHero = i === 0;
                const cardHref = job.slug ? `/jobs/${job.slug}` : (job.link || '#');
                const cardTarget = job.slug ? '_self' : '_blank';
                const daysAgo = job.created_at
                  ? Math.max(0, Math.floor((Date.now() - new Date(job.created_at).getTime()) / 864e5))
                  : null;
                return (
                  <a
                    key={job.id}
                    href={cardHref}
                    target={cardTarget}
                    rel="noopener noreferrer"
                    className={`group relative block rounded-2xl overflow-hidden bg-gradient-to-br from-white/[0.07] to-white/[0.02] hover:from-white/[0.12] hover:to-white/[0.04] backdrop-blur-sm border border-white/10 hover:border-sky-400/50 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-sky-500/20 ${
                      isHero ? 'lg:col-span-2 lg:row-span-2' : ''
                    }`}
                    data-testid={`hp-job-${job.id}`}
                  >
                    {isHero && (
                      <>
                        <div className="relative h-64 lg:h-72 bg-slate-900 overflow-hidden">
                          <img
                            src={job.cover_image || job.image_url || 'https://customer-assets.emergentagent.com/job_e6bd1d26-d4b0-49be-b226-7ca558f827f9/artifacts/9bhssqo2_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2005_04_33%20PM.png'}
                            alt={`${job.title} — ${job.employer || 'Paisley Scotland'}`}
                            loading="lazy"
                            onError={(e) => {
                              if (!e.target.dataset.fbk) {
                                e.target.dataset.fbk = '1';
                                e.target.src = 'https://customer-assets.emergentagent.com/job_e6bd1d26-d4b0-49be-b226-7ca558f827f9/artifacts/9bhssqo2_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2005_04_33%20PM.png';
                              }
                            }}
                            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent" />
                          <Badge className="absolute top-4 left-4 bg-emerald-500 text-white border-0 shadow-lg">
                            <Sparkles className="h-3 w-3 mr-1" /> Featured
                          </Badge>
                          {daysAgo != null && daysAgo <= 2 && (
                            <Badge className="absolute top-4 right-4 bg-rose-500 text-white border-0 shadow-lg animate-pulse">
                              New
                            </Badge>
                          )}
                        </div>
                        <div className="p-6 lg:p-8">
                          <h3 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-3 line-clamp-2 group-hover:text-sky-300 transition-colors">
                            {job.title}
                          </h3>
                          <div className="flex flex-wrap gap-3 text-sm text-slate-300 mb-4">
                            {job.employer && (
                              <span className="flex items-center gap-1.5 font-medium">
                                <Building2 className="h-4 w-4 text-sky-400" /> {job.employer}
                              </span>
                            )}
                            {job.location && (
                              <span className="flex items-center gap-1.5">
                                <MapPin className="h-4 w-4 text-sky-400" /> {job.location}
                              </span>
                            )}
                            {daysAgo != null && (
                              <span className="flex items-center gap-1.5 text-slate-400">
                                <Clock className="h-4 w-4" /> Posted {daysAgo === 0 ? 'today' : `${daysAgo}d ago`}
                              </span>
                            )}
                          </div>
                          {job.description && (
                            <p className="text-slate-400 text-sm line-clamp-2 mb-5">{job.description}</p>
                          )}
                          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-500 to-indigo-500 group-hover:from-sky-400 group-hover:to-indigo-400 text-white font-semibold px-5 py-2.5 rounded-full shadow-lg shadow-sky-500/30 text-sm transition-all">
                            View this role <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </>
                    )}

                    {!isHero && (
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="h-11 w-11 rounded-lg bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-lg shadow-sky-500/20">
                            {(job.employer || job.title || '?').charAt(0).toUpperCase()}
                          </div>
                          {daysAgo != null && daysAgo <= 2 && (
                            <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/30 text-[10px]">New</Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-white text-[15px] mb-2 line-clamp-2 group-hover:text-sky-300 transition-colors leading-snug">
                          {job.title}
                        </h3>
                        <div className="space-y-1.5 text-xs text-slate-400 mb-3">
                          {job.employer && (
                            <div className="flex items-center gap-1.5">
                              <Building2 className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{job.employer}</span>
                            </div>
                          )}
                          {job.location && (
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{job.location}</span>
                            </div>
                          )}
                        </div>
                        <div className="pt-3 border-t border-white/5 flex items-center justify-between">
                          <span className="text-xs text-slate-500">
                            {daysAgo != null ? (daysAgo === 0 ? 'Today' : `${daysAgo}d ago`) : 'Recent'}
                          </span>
                          <span className="text-xs font-medium text-sky-400 flex items-center gap-1 group-hover:gap-2 transition-all">
                            Apply <ArrowRight className="h-3 w-3" />
                          </span>
                        </div>
                      </div>
                    )}
                  </a>
                );
              })}
            </div>

            {/* Prominent bottom CTA */}
            <div className="text-center">
              <Link to="/jobs">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-sky-500 via-cyan-400 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 text-white font-semibold text-base px-10 py-6 rounded-full shadow-xl shadow-sky-500/40 hover:shadow-sky-400/50 transition-all"
                  data-testid="hp-jobs-cta-primary"
                >
                  Browse all {jobs.length} jobs <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <p className="mt-4 text-xs text-slate-500">
                Employers:{' '}
                <Link to="/jobs/post" className="text-sky-400 hover:text-sky-300 underline font-semibold" data-testid="hp-post-job-link">
                  post a job for £15
                </Link>{' '}
                — live in 30 seconds, mirrored to Paisley.org.uk automatically.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Properties Section — mirrors Jobs style, warmer palette */}
      {properties.length > 0 && (
        <section
          className="relative py-20 overflow-hidden bg-gradient-to-br from-slate-900 via-sky-950 to-slate-900"
          data-testid="properties-section"
        >
          {/* Decorative ambient glows */}
          <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-sky-500/15 blur-3xl pointer-events-none" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
              <div>
                <Badge className="mb-4 bg-amber-400/15 text-amber-300 border-amber-300/30 backdrop-blur">
                  <Home className="h-3 w-3 mr-1.5" /> Quality homes · Personal service
                </Badge>
                <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white tracking-tight">
                  Properties to Let in <span className="text-amber-300">Paisley</span>
                </h2>
                <p className="mt-3 text-slate-300 max-w-xl">
                  Live rentals mirrored from Paisley.org.uk — refreshed every page load.
                </p>
              </div>
              <Link to="/properties" className="flex-shrink-0">
                <Button size="lg" className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-slate-900 font-semibold shadow-lg shadow-amber-500/30">
                  Browse all properties <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {properties.map((p, i) => {
                const beds = p.bedrooms ?? p.beds;
                const rent = p.rent_pcm ?? p.price;
                const img = p.image || p.image_url || PROPERTY_FALLBACK;
                return (
                  <a
                    key={`${p.url || p.title}-${i}`}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative block rounded-2xl overflow-hidden bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/10 hover:border-amber-300/50 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-500/10"
                    data-testid={`hp-property-${i}`}
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <div className="relative h-36 overflow-hidden bg-slate-800">
                      <img
                        src={img}
                        alt={p.title || 'Property'}
                        loading="lazy"
                        onError={(e) => { e.target.src = PROPERTY_FALLBACK; }}
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {p.property_type && (
                        <Badge className="absolute top-2 left-2 bg-slate-900/80 text-white border-0 text-[10px] capitalize backdrop-blur">
                          {p.property_type}
                        </Badge>
                      )}
                      {rent && (
                        <div className="absolute bottom-2 right-2 bg-amber-400 text-slate-900 px-2.5 py-1 rounded-full text-xs font-semibold shadow">
                          £{rent} pcm
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-white text-sm mb-2 line-clamp-2 group-hover:text-amber-300 transition-colors">
                        {p.title}
                      </h3>
                      <div className="space-y-1 text-xs text-slate-400">
                        {beds != null && (
                          <div className="flex items-center gap-1.5">
                            <BedDouble className="h-3 w-3 flex-shrink-0" />
                            <span>{beds} bed{beds === 1 ? '' : 's'}</span>
                          </div>
                        )}
                        {(p.address || p.area) && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{p.address || p.area}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Sponsorship CTA (sourced from Paisley.org.uk) */}
      <section className="py-24 bg-slate-50" data-testid="tiers-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-sky-100 text-sky-700 border-sky-200">
            <Sparkles className="h-3 w-3 mr-1" /> Partnership Opportunities
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Sponsorship Packages
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto mb-2">
            Four tiers · Bronze · Silver · Gold · Platinum — backed by{' '}
            <a
              href="https://paisley.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-600 hover:text-sky-700 underline font-medium"
            >
              Paisley.org.uk
            </a>{' '}
            for visibility across our network of local sites.
          </p>
          <p className="text-slate-500 text-sm mb-10">
            See the live tier benefits and pricing — sign-up is handled on Paisley.org.uk.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/sponsors">
              <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white shadow-lg shadow-sky-500/20">
                View Sponsorship Packages <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="https://paisley.org.uk/sponsors">
              <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
                Go straight to sign-up
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why Sponsor Section */}
      <section className="py-24 bg-white" data-testid="why-sponsor-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Why Partner With <span className="text-sky-500">Paisley.org.uk</span>?
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Join a growing network of businesses making an impact in Paisley. Our platform 
                connects you directly with locals, tourists, and fellow businesses in Scotland's 
                historic textile town.
              </p>
              <ul className="space-y-4">
                {[
                  'Direct access to 25,000+ monthly website visitors',
                  'Featured placement across our event listings',
                  'Social media promotion to engaged followers',
                  'Networking opportunities with local businesses',
                  'Dedicated support for your promotional needs'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-sky-600" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <Link to="/contact">
                  <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white">
                    Talk to Our Team
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              {/* Cinematic image with slow ken-burns zoom + warm-to-cool gradient */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-900/10 aspect-[4/5] sm:aspect-[3/4]">
                <img
                  src={NETWORKING_IMG}
                  alt="Paisley skyline — Paisley Abbey, Anchor Mills and the Renfrewshire hills"
                  className="absolute inset-0 w-full h-full object-cover animate-kenburns"
                  loading="lazy"
                />
                {/* Sky→indigo wash so it bridges into the dark Jobs section visually */}
                <div className="absolute inset-0 bg-gradient-to-tr from-sky-900/40 via-transparent to-indigo-900/30 mix-blend-multiply pointer-events-none" />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-slate-900/70 to-transparent pointer-events-none" />

                {/* Decorative paisley swirl watermark */}
                <svg
                  viewBox="0 0 100 100"
                  className="absolute top-4 right-4 h-12 w-12 text-white/30 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M50 8 C82 12 88 44 72 68 C64 80 44 86 32 92 C20 96 18 104 28 104" strokeLinecap="round" />
                  <path d="M45 36 C57 36 65 46 61 58 C57 66 47 66 43 60" strokeLinecap="round" opacity="0.7" />
                  <circle cx="51" cy="50" r="3" fill="currentColor" stroke="none" />
                </svg>

                {/* On-image overline */}
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <p className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-sky-200/90 font-medium mb-1">
                    Paisley · Renfrewshire · Scotland
                  </p>
                  <p className="font-heading text-xl sm:text-2xl font-bold leading-tight drop-shadow-lg">
                    Where heritage meets ambition.
                  </p>
                </div>
              </div>

              {/* Floating animated stat — Active Partners */}
              <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-white p-4 sm:p-5 rounded-2xl shadow-2xl ring-1 ring-slate-200 animate-float-slow">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br from-sky-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/30">
                    <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-accent text-xl sm:text-2xl font-bold text-slate-900 leading-none">
                      <CountUp end={50} suffix="+" />
                    </div>
                    <div className="text-slate-500 text-xs sm:text-sm">Active Partners</div>
                  </div>
                </div>
              </div>

              {/* Floating animated stat — Live Jobs */}
              <div className="hidden sm:block absolute -top-5 -right-4 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-2xl ring-1 ring-white/10 animate-float-medium">
                <div className="flex items-center gap-2.5">
                  <div className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
                  </div>
                  <div>
                    <div className="font-accent text-base font-bold leading-none">
                      <CountUp end={jobs.length || 27} /> live jobs
                    </div>
                    <div className="text-slate-400 text-[10px] mt-0.5">Updated daily</div>
                  </div>
                </div>
              </div>

              {/* Floating animated stat — Sponsors marquee tease */}
              <div className="hidden lg:block absolute top-1/2 -right-6 -translate-y-1/2 bg-white/95 backdrop-blur p-3 rounded-xl shadow-xl ring-1 ring-slate-200 animate-float-fast">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  <span className="text-xs font-semibold text-slate-700 whitespace-nowrap">Backed by local sponsors</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-sky-500" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6">
            <Mail className="h-8 w-8 text-white" />
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Stay Updated
          </h2>
          <p className="text-sky-100 mb-8 max-w-xl mx-auto">
            Subscribe to our newsletter for the latest events, sponsorship opportunities, 
            and community news from Paisley.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 h-12 bg-white/90 border-0 text-slate-900 placeholder:text-slate-500"
              data-testid="newsletter-email-input"
              required
            />
            <Button 
              type="submit" 
              disabled={loading}
              className="h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="newsletter-submit-btn"
            >
              {loading ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
        </div>
      </section>

      {/* Advertisers strip — full list mirrored from paisley.org.uk */}
      {partners.length > 0 && (
        <section className="py-16 bg-white border-y border-slate-200" data-testid="featured-partners-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <Badge className="mb-3 bg-sky-100 text-sky-700 border-sky-200">
                <Sparkles className="h-3 w-3 mr-1" /> Trusted Partners
              </Badge>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
                Trusted by businesses across Paisley
              </h2>
              <p className="text-sm text-slate-500 mt-2">
                Local businesses advertising with our sister site <span className="font-medium">paisley.org.uk</span>
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {partners.map((p, idx) => {
                const rawLogo = p.image || p.logo || '';
                const logoSrc = rawLogo?.startsWith('http')
                  ? rawLogo
                  : `https://paisley.org.uk${rawLogo || ''}`;
                const cleanLink = (p.link || '').replace(/[\u2060\u200B]/g, '').trim() || '#';
                return (
                  <a
                    key={p.id || `${p.name}-${idx}`}
                    href={cleanLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative bg-white border border-slate-200 hover:border-sky-300 hover:shadow-lg rounded-xl p-5 flex flex-col items-center justify-center transition-all hover:-translate-y-1"
                    data-testid={`featured-partner-${idx}`}
                  >
                    {rawLogo ? (
                      <img
                        src={logoSrc}
                        alt={p.name}
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }}
                        className="max-h-16 w-auto object-contain mb-2 grayscale group-hover:grayscale-0 transition"
                      />
                    ) : (
                      <div className="h-16 flex items-center justify-center text-slate-300 text-2xl font-bold">
                        {(p.name || '?').charAt(0)}
                      </div>
                    )}
                    <p className="text-xs text-slate-600 text-center font-medium line-clamp-1">
                      {p.name}
                    </p>
                  </a>
                );
              })}
            </div>

            <div className="mt-10 text-center">
              <p className="text-sm text-slate-500 mb-4">
                Advertisers managed by{' '}
                <a
                  href="https://paisley.org.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-600 hover:text-sky-700 underline font-medium"
                >
                  Paisley.org.uk
                </a>{' '}
                — advertise with them at{' '}
                <a
                  href="https://paisley.org.uk/sponsors"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-600 hover:text-sky-700 underline font-medium"
                >
                  paisley.org.uk/sponsors
                </a>
                .
              </p>
              <a href="https://paisley.org.uk/sponsors" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="partners-become-cta">
                  Become a Featured Partner <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Recommended Tools — sponsored partner banners */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white" data-testid="recommended-tools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="mb-3 bg-indigo-100 text-indigo-700 border-indigo-200">
              <Sparkles className="h-3 w-3 mr-1" /> Recommended Tools
            </Badge>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              Growth tools we recommend for local businesses
            </h2>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl mx-auto">
              Hand-picked partners helping Paisley&apos;s businesses win more customers online.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a
              href="https://pathexecute.com/"
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="group relative block overflow-hidden rounded-2xl bg-slate-900 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-sky-400/50"
              data-testid="recommended-tool-pathexecute"
              aria-label="Visit PathExecute — AI Funnel Builder"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_e6bd1d26-d4b0-49be-b226-7ca558f827f9/artifacts/dqgoqprb_PathExecute%20promo.png"
                alt="PathExecute — AI Funnel Builder. Define the Path. Execute the Sale."
                loading="lazy"
                className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-500"
              />
              <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full opacity-80 group-hover:opacity-100 transition-opacity">
                Sponsored
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-sky-400 via-cyan-400 to-sky-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            <a
              href="https://instavibe.co.uk/"
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="group relative block overflow-hidden rounded-2xl bg-slate-900 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-fuchsia-400/50"
              data-testid="recommended-tool-instavibe"
              aria-label="Visit InstaVibe — Instagram DM Automation"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_e6bd1d26-d4b0-49be-b226-7ca558f827f9/artifacts/qodrpaf2_InstaVibe%20promo.png"
                alt="InstaVibe — Instagram DM Automation. Turn comments into customers."
                loading="lazy"
                className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-500"
              />
              <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full opacity-80 group-hover:opacity-100 transition-opacity">
                Sponsored
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-fuchsia-500 via-pink-400 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>
      </section>

      {/* Events — prime placement, matches Jobs/Properties dynamic style */}
      {events.length > 0 && (
        <section
          className="relative py-20 overflow-hidden bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900"
          data-testid="events-section"
        >
          <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-teal-400/10 blur-3xl pointer-events-none" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
              <div>
                <Badge className="mb-4 bg-emerald-400/15 text-emerald-300 border-emerald-300/30 backdrop-blur">
                  <Calendar className="h-3 w-3 mr-1.5" /> What's on this week
                </Badge>
                <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white tracking-tight">
                  Events in <span className="text-emerald-300">Paisley</span>
                </h2>
                <p className="mt-3 text-slate-300 max-w-xl">
                  Live event listings mirrored from Paisley.org.uk — refreshed on every visit.
                </p>
              </div>
              <Link to="/events" className="flex-shrink-0">
                <Button size="lg" className="bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-300 hover:to-teal-400 text-slate-900 font-semibold shadow-lg shadow-emerald-500/30">
                  See all events <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((ev, i) => {
                const d = ev.date ? new Date(ev.date) : null;
                const day = d && !Number.isNaN(d.getTime()) ? d.getDate() : null;
                const month = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-GB', { month: 'short' }) : null;
                return (
                  <a
                    key={`${ev.url || ev.title}-${i}`}
                    href={ev.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative block rounded-2xl overflow-hidden bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/10 hover:border-emerald-300/50 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-emerald-500/10 p-5"
                    data-testid={`hp-event-${i}`}
                  >
                    <div className="flex gap-4">
                      {day && (
                        <div className="flex-shrink-0 w-16 text-center bg-emerald-400/10 rounded-lg p-2 border border-emerald-400/20">
                          <p className="text-[10px] text-emerald-300 font-medium uppercase tracking-wide">{month}</p>
                          <p className="text-2xl font-bold text-white leading-none mt-1">{day}</p>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        {ev.category && (
                          <Badge className="mb-1.5 bg-emerald-500/10 text-emerald-300 border-emerald-500/20 text-[10px] capitalize">
                            {ev.category}
                          </Badge>
                        )}
                        <h3 className="font-semibold text-white text-sm mb-1.5 line-clamp-2 group-hover:text-emerald-300 transition-colors">
                          {ev.title}
                        </h3>
                        {ev.venue && (
                          <p className="text-xs text-slate-400 flex items-center gap-1">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{ev.venue}</span>
                          </p>
                        )}
                        {ev.time && (
                          <p className="text-xs text-slate-500 mt-0.5">{ev.time}</p>
                        )}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Latest News — clean editorial grid, live from paisley.org.uk */}
      {news.length > 0 && (
        <section className="py-20 bg-white border-t border-slate-100" data-testid="news-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
              <div>
                <Badge className="mb-3 bg-sky-100 text-sky-700 border-sky-200">
                  <Rss className="h-3 w-3 mr-1" /> Latest from Paisley
                </Badge>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                  Paisley News & Stories
                </h2>
                <p className="mt-2 text-slate-600 max-w-xl">
                  News, history and features straight from the paisley.org.uk newsroom.
                </p>
              </div>
              <Link to="/news">
                <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
                  All news <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.slice(0, 6).map((post, i) => {
                const dateStr = post.created_at
                  ? new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : null;
                const isLocal = post.origin === 'local';
                const href = isLocal ? `/news/${post.slug}` : `https://paisley.org.uk/news/${post.slug}`;
                const linkProps = isLocal
                  ? {}
                  : { target: '_blank', rel: 'noopener noreferrer' };
                return (
                  <a
                    key={post.id || i}
                    href={href}
                    {...linkProps}
                    className="group block"
                    data-testid={`hp-news-${i}`}
                  >
                    <Card className="h-full hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden">
                      {post.featured_image ? (
                        <div className="h-44 bg-slate-100 overflow-hidden">
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            loading="lazy"
                            onError={(e) => { e.target.style.display = 'none'; }}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      ) : (
                        <div className="h-44 bg-gradient-to-br from-sky-100 to-slate-100 flex items-center justify-center">
                          <Newspaper className="h-8 w-8 text-slate-300" />
                        </div>
                      )}
                      <CardContent className="p-5">
                        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                          {isLocal ? (
                            <Badge className="text-[10px] bg-sky-100 text-sky-700 border-sky-200">
                              Paisley Business
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] text-slate-500">
                              via Paisley.org.uk
                            </Badge>
                          )}
                          {post.category && (
                            <Badge variant="outline" className="text-[10px] capitalize">
                              {String(post.category).replace('-', ' ')}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-heading text-base font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-sky-600 transition-colors">
                          {post.title}
                        </h3>
                        {post.excerpt && (
                          <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                            {post.excerpt}
                          </p>
                        )}
                        <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                          <span>{dateStr}</span>
                          <span className="text-sky-600 font-medium flex items-center gap-1">
                            Read <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Grow Your Business in Paisley?
          </h2>
          <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
            Join our community of sponsors and reach thousands of potential customers. 
            Start your journey today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/sponsors" data-testid="cta-become-sponsor">
              <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white shadow-lg shadow-sky-500/30 px-10 py-6 text-lg">
                Become a Sponsor Today
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="cta-contact">
              <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 px-10 py-6 text-lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
